<script lang="ts">
  import Input from "$lib/components/ui/input/input.svelte";
  import * as Card from "$lib/components/ui/card";
  import Button from "$lib/components/ui/button/button.svelte";
  import SelectCustomer from "$src/presentation/customers/components/select-customer.svelte";
  import { getCustomerInfo } from "$src/domain/customers/utils/customer-info-utils";
  import TimeSince from "$src/presentation/core/time-since.svelte";
  import { type CustomerSummary } from "$src/domain/customers/CustomerSummary";
  import AddressValue from "$src/presentation/core/address-value.svelte";
  import { t } from "svelte-i18n";
  import SelectStore from "$src/presentation/groupstores/select-store.svelte";
  import { type StoreSummary } from "$src/domain/directory/stores/StoreSummary";
  import SelectFolder from "$src/presentation/customers/components/select-folder.svelte";
  import * as zod from "zod";
  import { createForm } from "felte";
  import { validator } from "@felte/validator-zod";
  import { type CreateProjectData } from "$src/domain/projects/CreateProjectData";
  import { useCreateProject } from "../../hooks/useCreateProject";
  import { push } from "svelte-spa-router";
  import { useRequestDraft } from "../../edit/hooks/useRequestDraft";
  import { onMount } from "svelte";
  import Label from "$lib/components/ui/label/label.svelte";
  import { cn } from "$lib/utils";
  import SelectProjectType from "$src/presentation/customers/components/select-project-type.svelte";
  import { projectTypeSchema } from "$src/domain/customers/ProjectType";

  export let groupStoreId: number;
  export let stores: StoreSummary[];

  let selectedCustomer: CustomerSummary | undefined;
  let selectedStore: StoreSummary | undefined;

  onMount(() => {
    selectedStore = stores[0];
    $data.storeId = selectedStore.storeId;
  });

  const schema = zod.object({
    storeId: zod.number({
      required_error: $t("page.projects.new.store-required"),
      invalid_type_error: $t("page.projects.new.store-required"),
    }),
    customerId: zod.number({
      required_error: $t("page.projects.new.customer-required"),
      invalid_type_error: $t("page.projects.new.customer-required"),
    }),
    projectName: zod
      .string()
      .min(1, $t("page.projects.new.foldername-required"))
      .max(50, $t("page.projects.new.foldername-max-length")),
    projectType: projectTypeSchema,
    name: zod
      .string()
      .min(1, $t("page.projects.new.name-required"))
      .max(200, $t("page.projects.new.name-max-length")),
  }).refine(
    (data) => {

      return data.projectType?.id > 0;
    },
    {
      message: $t("page.projects.new.project-type-required"),
      path: ["projectType"],
    }
  );


  const mutation = useCreateProject();

  type FormValues = zod.infer<typeof schema>;

  $: editForm = createForm({
    initialValues: {},
    onSubmit: handleSubmit,
    extend: validator({ schema }),
  });

  $: form = editForm.form;
  $: data = editForm.data;

  $: {
    if (selectedStore?.storeId) {
      $data.storeId = selectedStore.storeId;
    }
  }

  $: errors = editForm.errors;

  function customerSelected(event: CustomEvent<CustomerSummary>) {
    selectedCustomer = event.detail;
    $data.customerId = selectedCustomer.id;
  }

  const requestDraftCommand = useRequestDraft();

  async function handleSubmit(values: FormValues) {
    var customerId = selectedCustomer?.id;
    var storeId = selectedStore?.storeId;

    if (!customerId || !storeId) return;

    var data: CreateProjectData = {
      customerId: customerId,
      storeId: storeId,
      projectName: values.projectName,
      projectType: values.projectType.id,
      versionName: values.name,
    };

    var result = await $mutation.mutateAsync({
      groupStoreId: groupStoreId,
      data,
    });

    $requestDraftCommand.mutate({ groupStoreId, projectId: result.identifier });

    push(`/gs/${groupStoreId}/projects/${result.identifier}`);
  }

  function cancelCreate() {
    history.back();
  }
</script>

<Card.Root class="max-w-4xl m-auto">
  <Card.Header>
    <Card.Title class="flex flew-row w-full justify-between">
      {$t("page.projects.new.title")}
    </Card.Title>
  </Card.Header>
  <Card.Content class="min-w-[370px]">
    <form use:form>
      <div class="flex w-full text-sm">
        <div
          class="grid grid-cols-1 md:grid-cols-[150px_1fr] gap-y-2 md:gap-4 w-full px-4 items-center"
        >
          <Label class="text-xs text-muted-foreground md:text-right">
            {$t("page.projects.new.store")}
          </Label>
          <div>
            {#if stores.length > 1}
              <SelectStore bind:value={selectedStore} />
            {:else}
              {selectedStore?.name}
            {/if}
            {#if $errors.storeId}
              <div class="col-span-4 text-right text-xs text-red-500">
                {$errors.storeId}
              </div>
            {/if}
          </div>
          <Label class="text-xs text-muted-foreground md:text-right">
            {$t("page.projects.new.customer")}
          </Label>
          <div class="flex flex-col gap-2">
            <SelectCustomer
              {groupStoreId}
              defaultCountryCode={selectedStore?.countryCode}
              on:selectCustomer={customerSelected}
            />
            {#if selectedCustomer}
              <div class="flex-1 space-y-1 border rounded-lg p-4">
                <div class="flex w-full flex-col gap-1">
                  <div class="flex items-center">
                    <div class="flex items-center gap-2">
                      <div class="font-semibold">
                        {getCustomerInfo(selectedCustomer)}
                      </div>
                    </div>
                    <div class="ml-auto text-xs text-muted-foreground">
                      <TimeSince
                        dateString={selectedCustomer.lastProjectDate}
                      />
                    </div>
                  </div>
                </div>
                <div class="flex w-full flex-col gap-1">
                  <div class="flex items-center">
                    <div class="line-clamp-2 text-xs text-muted-foreground">
                      <AddressValue value={selectedCustomer.billingAddress} />
                    </div>
                    <div class="ml-auto text-xs text-muted-foreground">
                      {selectedCustomer.orderCount ?? 0}
                      {$t("page.search.results.sales")}
                    </div>
                  </div>
                </div>
              </div>
            {/if}
            {#if $errors.customerId}
              <div class="col-span-4 text-right text-xs text-red-500">
                {$errors.customerId}
              </div>
            {/if}
          </div>
          <Label class="text-xs text-muted-foreground md:text-right">
            {#if selectedCustomer}
              {$t("page.projects.new.folder")}
            {/if}
          </Label>
          <div>
            {#if selectedCustomer}
              <SelectFolder
                bind:value={$data.projectName}
                {groupStoreId}
                customerId={selectedCustomer?.id}
              />
            {/if}
            {#if $errors.projectName}
              <div class="col-span-4 text-right text-xs text-red-500">
                {$errors.projectName}
              </div>
            {/if}
          </div>
          <Label class="text-xs text-muted-foreground md:text-right">
            {#if selectedCustomer}
              {$t("page.projects.new.folder-type")}
            {/if}
          </Label>
          <div>
            {#if selectedCustomer}
              <SelectProjectType
                bind:value={$data.projectType}
                {groupStoreId}
              />
            {/if}
            {#if $errors.projectType}
              <div class="col-span-4 text-right text-xs text-red-500">
                {$t("page.projects.new.project-type-required")}
              </div>
            {/if}
          </div>
          <Label class="text-xs text-muted-foreground md:text-right">
            {$t("page.projects.new.name")}
          </Label>
          <div>
            <Input
              bind:value={$data.name}
              name="name"
              maxlength={200}
              class={cn($errors.name ? "border-red-500" : "")}
            />
            {#if $errors.name}
              <div class="col-span-4 text-right text-xs text-red-500">
                {$errors.name}
              </div>
            {/if}
          </div>
          <div class="md:col-span-2 flex justify-end">
            <Button type="submit" class="px-8"
              >{$t("page.projects.new.create")}</Button
            >
            <Button variant="ghost" on:click={cancelCreate}
              >{$t("global.messages.cancel")}</Button
            >
          </div>
        </div>
      </div>
    </form>
  </Card.Content>
  <Card.Footer class="flex"></Card.Footer>
</Card.Root>
